/** Trial runner - timed 140-question ADC-style paper with autosave, flags, navigator, locked review. */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import QuestionImage from "@/components/QuestionImage";
import { Clock, Flag, ChevronLeft, ChevronRight, CheckCircle2, XCircle, Loader2, ArrowLeft } from "lucide-react";
import { ShareScoreButton } from "@/components/ShareScoreButton";

const fmtTime = (s) => {
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  return `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
};

const SubjectBars = ({ subjectResults }) => (
  <div className="space-y-2">
    {Object.entries(subjectResults || {}).sort((a, b) => (a[1].correct / a[1].total) - (b[1].correct / b[1].total)).map(([sid, r]) => {
      const pct = r.total ? Math.round((r.correct / r.total) * 100) : 0;
      return (
        <div key={sid} className="flex items-center gap-3 text-sm">
          <span className="w-40 truncate capitalize text-muted-foreground">{sid.replace(/_/g, " ")}</span>
          <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
            <div className={`h-full ${pct >= 65 ? "bg-success" : "bg-warning"}`} style={{ width: `${pct}%` }} />
          </div>
          <span className="w-14 text-right font-medium">{pct}%</span>
        </div>
      );
    })}
  </div>
);

export default function TrialRunnerPage() {
  const { trialNumber } = useParams();
  const n = parseInt(trialNumber, 10);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [phase, setPhase] = useState(searchParams.get("review") ? "review" : "loading"); // loading|running|submitting|results|review
  const [trial, setTrial] = useState(null);
  const [answers, setAnswers] = useState({});
  const [flagged, setFlagged] = useState(new Set());
  const [idx, setIdx] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [results, setResults] = useState(null);
  const [review, setReview] = useState(null);
  const timerRef = useRef(null);
  const submittingRef = useRef(false);

  const doSubmit = useCallback(async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setPhase("submitting");
    clearInterval(timerRef.current);
    try {
      const r = await api.post(`/trials/${n}/submit`);
      setResults(r);
      setPhase("results");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submit failed");
      submittingRef.current = false;
      setPhase("running");
    }
  }, [n]);

  const loadReview = useCallback(async () => {
    try {
      const r = await api.get(`/trials/${n}/review`);
      setReview(r);
      setPhase("review");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Review unavailable");
      navigate("/trials");
    }
  }, [n, navigate]);

  useEffect(() => {
    if (searchParams.get("review")) { loadReview(); return; }
    (async () => {
      try {
        const r = await api.post(`/trials/${n}/start`);
        if (r.auto_submitted) { setResults(r); setPhase("results"); return; }
        setTrial(r);
        setAnswers(r.answers || {});
        setFlagged(new Set(r.flagged || []));
        setTimeLeft(r.time_remaining_seconds);
        setPhase("running");
      } catch (e) {
        const d = e?.response?.data?.detail;
        if (e?.response?.status === 402) {
          toast.error("Pro feature", { description: d?.message || "Upgrade to unlock this trial." });
          navigate("/billing");
        } else if (e?.response?.status === 409) {
          loadReview();
        } else {
          toast.error(typeof d === "string" ? d : "Could not start trial");
          navigate("/trials");
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [n]);

  useEffect(() => {
    if (phase !== "running") return;
    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) { doSubmit(); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [phase, doSubmit]);

  const selectOption = (qid, oid) => {
    setAnswers((a) => ({ ...a, [qid]: oid }));
    api.post(`/trials/${n}/answer`, { question_id: qid, option_id: oid }).catch(() => {});
  };

  const toggleFlag = (qid) => {
    const next = new Set(flagged);
    const nowFlagged = !next.has(qid);
    if (nowFlagged) next.add(qid); else next.delete(qid);
    setFlagged(next);
    api.post(`/trials/${n}/flag`, { question_id: qid, flagged: nowFlagged }).catch(() => {});
  };

  if (phase === "loading" || phase === "submitting") {
    return (
      <div className="py-24 flex flex-col items-center gap-3 text-muted-foreground" data-testid="trial-loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <p>{phase === "submitting" ? "Scoring your trial…" : "Building your paper…"}</p>
      </div>
    );
  }

  if (phase === "results" && results) {
    return (
      <div className="max-w-2xl mx-auto space-y-6" data-testid="trial-results">
        <div className="rounded-2xl border border-border bg-card p-8 text-center">
          <p className="text-sm text-muted-foreground uppercase tracking-wider">Readiness Trial {n}</p>
          <p className="text-6xl font-bold mt-3" data-testid="trial-score">{results.score}%</p>
          <p className="text-muted-foreground mt-2">
            {results.correct}/{results.total} correct · {results.answered} answered · +{results.xp_earned} points
          </p>
        </div>
        <div className="rounded-2xl border border-border bg-card p-6">
          <h3 className="font-semibold mb-4">By subject</h3>
          <SubjectBars subjectResults={results.subject_results} />
        </div>
        <div className="flex gap-3 justify-center flex-wrap">
          <Button onClick={loadReview} data-testid="trial-open-review-btn">Open full review</Button>
          <ShareScoreButton trialNumber={n} score={results.score} correct={results.correct} total={results.total} />
          <Button variant="outline" onClick={() => navigate("/trials")}>Back to trials</Button>
        </div>
      </div>
    );
  }

  if (phase === "review" && review) {
    return (
      <div className="max-w-3xl mx-auto space-y-6" data-testid="trial-review">
        <div className="flex items-center justify-between">
          <button onClick={() => navigate("/trials")} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="w-4 h-4" /> Back to trials
          </button>
          <div className="flex items-center gap-3">
            <ShareScoreButton trialNumber={n} score={review.score} correct={review.correct} total={review.total} />
            <span className="text-sm font-semibold">Trial {n} · {review.score}% ({review.correct}/{review.total})</span>
          </div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-6">
          <h3 className="font-semibold mb-4">By subject</h3>
          <SubjectBars subjectResults={review.subject_results} />
        </div>
        <div className="space-y-4">
          {review.items.map((item, i) => {
            const q = item.question;
            return (
              <div key={q.id} className={`rounded-2xl border p-5 bg-card ${item.is_correct ? "border-success/30" : "border-destructive/30"}`}>
                <div className="flex items-start gap-3">
                  {item.is_correct
                    ? <CheckCircle2 className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                    : <XCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />}
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-muted-foreground mb-1">Q{i + 1} · {q.subject_id?.replace(/_/g, " ")}</p>
                    <p className="font-medium text-sm">{q.question_text}</p>
                    <div className="mt-3 space-y-1.5">
                      {(q.options || []).map((o) => {
                        const isCorrect = o.id === q.correct_option_id;
                        const isMine = o.id === item.user_answer;
                        return (
                          <div key={o.id} className={`text-sm px-3 py-2 rounded-lg border ${isCorrect ? "border-success/40 bg-success/10" : isMine ? "border-destructive/40 bg-destructive/10" : "border-border/40"}`}>
                            {o.text} {isCorrect && <span className="text-success text-xs font-semibold ml-1">✓ correct</span>}
                            {isMine && !isCorrect && <span className="text-destructive text-xs font-semibold ml-1">your answer</span>}
                          </div>
                        );
                      })}
                    </div>
                    {!item.user_answer && <p className="text-xs text-warning mt-2">Not answered</p>}
                    {q.explanation && <p className="text-xs text-muted-foreground mt-3">{q.explanation}</p>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // running
  const q = trial?.questions?.[idx];
  if (!q) return null;
  const answeredCount = Object.keys(answers).length;

  return (
    <div className="max-w-4xl mx-auto space-y-5" data-testid="trial-runner">
      {/* Header bar */}
      <div className="sticky top-0 z-10 rounded-2xl border border-border bg-card p-4 flex items-center justify-between gap-3">
        <div className="text-sm">
          <span className="font-semibold">Trial {n}</span>
          <span className="text-muted-foreground"> · Q{idx + 1}/{trial.questions.length} · {answeredCount} answered</span>
        </div>
        <div className="flex items-center gap-3">
          <span className={`inline-flex items-center gap-1.5 font-mono font-semibold ${timeLeft < 900 ? "text-destructive" : "text-foreground"}`} data-testid="trial-timer">
            <Clock className="w-4 h-4" /> {fmtTime(timeLeft)}
          </span>
          <Button
            size="sm"
            variant="outline"
            data-testid="trial-submit-btn"
            onClick={() => {
              const un = trial.questions.length - answeredCount;
              if (window.confirm(un > 0 ? `Submit now? ${un} unanswered question(s) will score zero.` : "Submit your trial?")) doSubmit();
            }}
          >
            Finish &amp; submit
          </Button>
        </div>
      </div>

      {/* Question */}
      <div className="rounded-2xl border border-border bg-card p-6" data-testid="trial-question-card">
        <div className="flex items-start justify-between gap-3 mb-4">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">{q.subject_id?.replace(/_/g, " ")}</p>
          <button
            onClick={() => toggleFlag(q.id)}
            data-testid="trial-flag-btn"
            className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border ${flagged.has(q.id) ? "border-warning/50 bg-warning/15 text-warning" : "border-border text-muted-foreground hover:text-foreground"}`}
          >
            <Flag className="w-3 h-3" /> {flagged.has(q.id) ? "Flagged" : "Flag"}
          </button>
        </div>
        {q.clinical_vignette && <p className="text-sm text-muted-foreground mb-3 italic">{q.clinical_vignette}</p>}
        {q.image_url && <QuestionImage src={q.image_url} caption={q.image_caption} />}
        <p className="font-medium mb-4">{q.question_text}</p>
        <div className="space-y-2">
          {(q.options || []).map((o) => (
            <button
              key={o.id}
              data-testid={`trial-option-${o.id}`}
              onClick={() => selectOption(q.id, o.id)}
              className={`w-full text-left px-4 py-3 rounded-xl border text-sm transition-colors ${answers[q.id] === o.id ? "border-primary bg-primary/10 font-medium" : "border-border hover:border-primary/40 hover:bg-secondary"}`}
            >
              {o.text}
            </button>
          ))}
        </div>
        <div className="flex justify-between mt-6">
          <Button variant="outline" size="sm" disabled={idx === 0} onClick={() => setIdx(idx - 1)} data-testid="trial-prev-btn" className="gap-1">
            <ChevronLeft className="w-4 h-4" /> Previous
          </Button>
          <Button size="sm" disabled={idx === trial.questions.length - 1} onClick={() => setIdx(idx + 1)} data-testid="trial-next-btn" className="gap-1">
            Next <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Navigator grid */}
      <div className="rounded-2xl border border-border bg-card p-4" data-testid="trial-navigator">
        <div className="flex flex-wrap gap-1.5">
          {trial.questions.map((qq, i) => (
            <button
              key={qq.id}
              onClick={() => setIdx(i)}
              className={`w-8 h-8 rounded-lg text-xs font-medium border transition-colors ${
                i === idx ? "border-primary bg-primary text-primary-foreground"
                : flagged.has(qq.id) ? "border-warning/50 bg-warning/15 text-warning"
                : answers[qq.id] ? "border-success/40 bg-success/15 text-success"
                : "border-border text-muted-foreground hover:border-primary/40"
              }`}
            >
              {i + 1}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
